import type { Drawing, DrawingStyle } from "lightweight-charts-drawing"
import { useEffect, useMemo, useRef, useState } from "react"
import { ColorSwatchButton, type PickerLineWidth } from "../../ui/ColorPicker.tsx"
import { Modal } from "../../ui/Modal.tsx"
import { fillColorOf, fillPatch, FONT_SIZES, settingsModel, textColorOf, textOf, withStyle, withText } from "./drawingSettings.ts"

type Tab = "style" | "text"

const WIDTHS: ReadonlyArray<PickerLineWidth> = [1, 2, 3, 4]
const asWidth = (width: number): PickerLineWidth => WIDTHS.find((w) => w === width) ?? 1

/**
 * TradingView's drawing settings: the Style tab (line colour with opacity,
 * thickness, line style, background) and the Text tab (the text, its colour,
 * size, bold, italic, and a line's label alignment). Edits show on the chart as
 * they're made; Cancel (or closing) puts the drawing back, Ok keeps them.
 */
export function DrawingSettingsDialog({ initial, onChange, onClose, title }: {
  readonly initial: Drawing
  readonly title: string
  /** Shows (and keeps, unless cancelled) the edited drawing. */
  readonly onChange: (drawing: Drawing) => void
  readonly onClose: () => void
}) {
  const model = useMemo(() => settingsModel(initial), [initial])
  const [tab, setTab] = useState<Tab>(model.hasText && !model.hasLine ? "text" : "style")
  const [drawing, setDrawing] = useState(initial)
  const style = drawing.style

  // One chart update per animation frame (the opacity slider fires on every pixel).
  const frame = useRef(0)
  const change = (next: Drawing) => {
    setDrawing(next)
    cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => onChange(next))
  }
  const changeStyle = (patch: Partial<DrawingStyle>) => change(withStyle(drawing, patch))
  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  const applied = useRef(false)
  // X, the backdrop, Escape, and Cancel all put the drawing back as it was.
  const close = () => {
    cancelAnimationFrame(frame.current)
    if (!applied.current) onChange(initial)
    onClose()
  }

  return (
    <Modal title={title} onClose={close}>
      <nav className="dialog-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "style"} onClick={() => setTab("style")}>Style</button>
        {model.hasText && (
          <button type="button" role="tab" aria-selected={tab === "text"} onClick={() => setTab("text")}>Text</button>
        )}
      </nav>
      <form
        noValidate
        className="settings-form"
        onSubmit={(event) => {
          event.preventDefault()
          applied.current = true
          cancelAnimationFrame(frame.current)
          onChange(drawing)
          onClose()
        }}
      >
        {tab === "style"
          ? (
            <div className="style-tab">
              {model.hasLine && (
                <div className="style-row">
                  <span>{model.isShape ? "Border" : "Line"}</span>
                  <ColorSwatchButton
                    title={model.isShape ? "Border colour" : "Line colour"}
                    color={style.color}
                    onColor={(color) => changeStyle({ color })}
                    width={asWidth(style.width)}
                    onWidth={(width) => changeStyle({ width })}
                    lineStyle={style.lineStyle}
                    onLineStyle={(lineStyle) => changeStyle({ lineStyle })}
                  />
                  <span />
                </div>
              )}
              {!model.hasLine && (
                <div className="style-row">
                  <span>Colour</span>
                  <ColorSwatchButton title="Colour" color={style.color} onColor={(color) => changeStyle({ color })} />
                  <span />
                </div>
              )}
              {model.hasBackground && (
                <div className="style-row">
                  <label className="style-check">
                    <input
                      type="checkbox"
                      checked={style.fillBackground !== false}
                      onChange={(event) => changeStyle({ fillBackground: event.target.checked })}
                    />
                    Background
                  </label>
                  <ColorSwatchButton
                    title="Background colour"
                    color={fillColorOf(drawing)}
                    onColor={(color) => changeStyle(fillPatch(color))}
                  />
                  <span />
                </div>
              )}
              {model.hasBorder && (
                <div className="style-row">
                  {style.drawBorder !== undefined
                    ? (
                      <label className="style-check">
                        <input
                          type="checkbox"
                          checked={style.drawBorder}
                          onChange={(event) => changeStyle({ drawBorder: event.target.checked })}
                        />
                        Border
                      </label>
                    )
                    : <span>Border</span>}
                  <ColorSwatchButton
                    title="Border colour"
                    color={style.borderColor ?? style.color}
                    onColor={(borderColor) => changeStyle(style.drawBorder !== undefined ? { borderColor, drawBorder: true } : { borderColor })}
                  />
                  <span />
                </div>
              )}
            </div>
          )
          : (
            <div className="style-tab">
              <textarea
                className="drawing-settings-text"
                aria-label="Text"
                placeholder="Add text"
                rows={3}
                value={textOf(drawing)}
                onChange={(event) => change(withText(drawing, event.target.value))}
              />
              <div className="style-row">
                <span>Text</span>
                <ColorSwatchButton
                  title="Text colour"
                  color={textColorOf(drawing)}
                  onColor={(color) => changeStyle({ [model.textColorKey]: color })}
                />
                <select
                  aria-label="Font size"
                  value={style.fontSize ?? 14}
                  onChange={(event) => changeStyle({ fontSize: Number(event.target.value) })}
                >
                  {FONT_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
              </div>
              <div className="style-row">
                <label className="style-check">
                  <input type="checkbox" checked={style.bold === true} onChange={(event) => changeStyle({ bold: event.target.checked })} />
                  Bold
                </label>
                <label className="style-check">
                  <input type="checkbox" checked={style.italic === true} onChange={(event) => changeStyle({ italic: event.target.checked })} />
                  Italic
                </label>
                <span />
              </div>
              {model.hasAlignment && (
                <div className="style-row">
                  <span>Alignment</span>
                  <select
                    aria-label="Vertical alignment"
                    value={style.vertLabelsAlign ?? "bottom"}
                    onChange={(event) => changeStyle({ vertLabelsAlign: event.target.value as NonNullable<DrawingStyle["vertLabelsAlign"]> })}
                  >
                    <option value="top">Top</option>
                    <option value="middle">Middle</option>
                    <option value="bottom">Bottom</option>
                  </select>
                  <select
                    aria-label="Horizontal alignment"
                    value={style.horzLabelsAlign ?? "center"}
                    onChange={(event) => changeStyle({ horzLabelsAlign: event.target.value as NonNullable<DrawingStyle["horzLabelsAlign"]> })}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              )}
            </div>
          )}
        <footer className="settings-actions">
          <span className="toolbar-spacer" />
          <button type="button" onClick={close}>Cancel</button>
          <button type="submit" className="primary">Ok</button>
        </footer>
      </form>
    </Modal>
  )
}
